import './CreateUserModal.scss';

import * as yup from 'yup';

import { Alert, Button, Col, Form, FormCheck, Modal, Row, Spinner } from 'react-bootstrap';
import { CreateUserApplicationRoles } from '~/components/UserPage/CreateUserApplicationRole/CreateUserApplicationRole';
import {
  CreateUserMutation,
  CreateUserMutationVariables,
  CreateUserMutation_createUser_user,
} from '~/graphqlTypes/CreateUserMutation';
import { Formik, FormikHelpers } from 'formik';
import { GrantUserRolesMutation, GrantUserRolesMutationVariables } from '~/graphqlTypes/GrantUserRolesMutation';

import { BEM } from '@feedfm/feed-util';
import React from 'react';
import { useHistory } from 'react-router';
import {
  ApplicationAndRoleWithPermissions,
  UserApplicationRoles,
} from '~/components/UserPage/UserApplicationRoles/UserApplicationRoles';
// Import the GraphQL query
import createUserQuery from './CreateUserMutation.gql';
import { getGraphQLErrorMessagesUi } from '~/util/errors';
import grantUserRolesMutation from './GrantUserRolesMutation.gql';
import { useAlert } from '~/hooks/useAlert';
import { useId } from '~/hooks/useId';
import { useSafeMutation } from '~/hooks/useSafeMutation';

// Form interface
interface UserFormValues {
  emailAddress: string;
  firstName: string;
  lastName: string;
  role: string;
  applicationRoles: ApplicationAndRoleWithPermissions[];
}

// Validation schema using Yup
const UserFormValidation = yup.object( {
  firstName: yup.string().required( 'First name is required' ),
  lastName: yup.string().required( 'Last name is required' ),
  emailAddress: yup.string().email( 'Please enter a valid email address' ).required( 'Email address is required' ),
  role: yup.string(),
  applicationRoles: yup.array().when( 'role', {
    is: ( role: string ) => !role,
    then: ( schema ) => schema.min( 1, 'External users must connect to at least one application' ),
    otherwise: ( schema ) => schema.notRequired(),
  } ),
} );

// Component props interface
export interface CreateUserModalProps {
  show?: boolean;
  onHide: () => void;
  onSuccess?: ( createdUser: CreateUserMutation_createUser_user ) => void;
  className?: string;
}

// Initial form values
const initialValues: UserFormValues = {
  emailAddress: '',
  firstName: '',
  lastName: '',
  role: 'GLOBAL_SUPERADMIN',
  applicationRoles: [],
};

export const DEFAULT_GLOBAL_ROLES = [
  {
    role: 'GLOBAL_SUPERADMIN',
    label: 'Super Admin - can do all the things',
  },
  {
    role: 'GLOBAL_INTERNAL_CURATOR',
    label: 'Internal Curator - can curate stations and music',
  },
  {
    role: 'GLOBAL_INTERNAL_CURATION_SUPPORT_AGENT',
    label: 'Internal Curation Support Agent - can do FP station review',
  },
  {
    role: '',
    label: 'External (non-Feed) user',
  },
];

const bem = new BEM( 'create-user-modal' );

/**
 * CreateUserModal component. This modal will create a new user in the database
 * when the user hits ok, and return it on `onSuccess`.
 */

export function CreateUserModal( { show, onHide, onSuccess, className }: CreateUserModalProps ) {
  const [createUser, { loading: createUserLoading }] = useSafeMutation<CreateUserMutation, CreateUserMutationVariables>(
    createUserQuery,
  );
  const [grantRoles, { loading: rolesLoading }] = useSafeMutation<
  GrantUserRolesMutation,
  GrantUserRolesMutationVariables
  >( grantUserRolesMutation );
  const id = useId();

  const alert = useAlert();
  const history = useHistory();

  // Handle form submission
  const handleSubmit = async (
    values: UserFormValues,
    { setFieldError, setSubmitting, setStatus }: FormikHelpers<UserFormValues>,
  ) => {
    const { data, error } = await createUser( {
      userForm: {
        firstName: values.firstName,
        lastName: values.lastName,
        emailAddress: values.emailAddress,
      },
      role: values.role,
    } );

    if ( error ) {
      setStatus( { errors: getGraphQLErrorMessagesUi( error ) } );
      setSubmitting( false );
      return;
    }

    // Handle field-specific errors from the server
    if ( data.createUser.errors?.length ) {
      const serverErrors = data.createUser.errors;

      // Map server errors to form fields
      serverErrors.forEach( ( serverError ) => {
        setFieldError( serverError.key, serverError.message );
      } );

      setSubmitting( false );
      return;
    }

    if ( !data.createUser.user ) {
      setStatus( { errors: getGraphQLErrorMessagesUi( 'Unable to load created user?' ) } );
      setSubmitting( false );
      return;
    }

    const user = data.createUser.user;

    if ( values.applicationRoles.length > 0 ) {
      // assign roles
      const { data: grantRolesData, error: grantRolesError } = await grantRoles( {
        userId: user.id.toString(),
        applicationRoles: values.applicationRoles.map( ( ar ) => ( {
          applicationId: ar.application.id.toString(),
          role: ar.role,
        } ) ),
      } );

      if ( grantRolesError ) {
        setStatus( { errors: getGraphQLErrorMessagesUi( grantRolesError ) } );
        setSubmitting( false );
        return;
      }

      if ( grantRolesData.grantRoles.__typename === 'RoleGrantError' ) {
        alert( `Problem assigning roles to user: ${grantRolesData.grantRoles.message}`, 'danger', false );
        setSubmitting( false );
        return;
      }
    }

    if ( onSuccess ) {
      onSuccess( user );
    }

    alert(
      {
        message: `Successfully created user ${user.firstName} ${user.lastName} (${user.emailAddress}). Redirecting to user page.`,
        link: `/user/${user.id}`,
      },
      'success',
    );

    history.push( `/user/${user.id}` );
  };

  const loading = createUserLoading || rolesLoading;

  return (
    <Modal show={ show } onHide={ onHide } className={ BEM.$( bem, className ) } data-testid="create-user-modal">
      <Formik
        initialValues={ initialValues }
        initialStatus={ undefined }
        validationSchema={ UserFormValidation }
        onSubmit={ handleSubmit }
        enableReinitialize
      >
        { ( {
          touched,
          values,
          errors,
          status,
          handleChange,
          handleSubmit: formikHandleSubmit,
          handleBlur,
          setStatus,
          setFieldValue,
        } ) => (
          <Form onSubmit={ formikHandleSubmit }>
            <Modal.Header closeButton>
              <Modal.Title>Create New User</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              { /* Error Alert */ }
              <Alert variant="danger" show={ !loading && Boolean( status?.errors ) }>
                <Alert.Heading>Error Creating User</Alert.Heading>
                { status?.errors }
              </Alert>

              { /* Form Fields */ }
              <Form.Group controlId={ `${id}-create-user-modal-emailAddress` }>
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="emailAddress"
                  value={ values.emailAddress }
                  onChange={ handleChange }
                  onBlur={ handleBlur }
                  isInvalid={ touched.emailAddress && !!errors.emailAddress }
                  disabled={ loading }
                />
                <Form.Control.Feedback type="invalid">{ errors.emailAddress }</Form.Control.Feedback>
              </Form.Group>

              <Form.Row>
                <Form.Group as={ Col } md={ 6 } controlId={ `${id}-create-user-modal-firstName` }>
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={ values.firstName }
                    onChange={ handleChange }
                    onBlur={ handleBlur }
                    isInvalid={ touched.firstName && !!errors.firstName }
                    disabled={ loading }
                  />
                  <Form.Control.Feedback type="invalid">{ errors.firstName }</Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={ Col } md={ 6 } controlId={ `${id}-create-user-modal-lastName` }>
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={ values.lastName }
                    onChange={ handleChange }
                    onBlur={ handleBlur }
                    isInvalid={ touched.lastName && !!errors.lastName }
                    disabled={ loading }
                  />
                  <Form.Control.Feedback type="invalid">{ errors.lastName }</Form.Control.Feedback>
                </Form.Group>
              </Form.Row>

              <Form.Row>
                <Form.Group as={ Col }>
                  <Form.Label>Global Role</Form.Label>
                  { DEFAULT_GLOBAL_ROLES.map( ( role ) => (
                    <FormCheck
                      key={ role.role }
                      type="radio"
                      id={ `${id}-global-role-${role.role}` }
                      name="role"
                      label={ role.label }
                      value={ role.role }
                      checked={ values.role === role.role }
                      onChange={ handleChange }
                      onBlur={ handleBlur }
                    />
                  ) ) }
                  <Form.Control.Feedback type="invalid">{ errors.role }</Form.Control.Feedback>
                </Form.Group>
              </Form.Row>

              { values.role === '' && (
                <>
                  { values.applicationRoles.length > 0 && (
                    <Row className="mt-4">
                      <Col>
                        <h5>User will be connected to:</h5>
                      </Col>
                    </Row>
                  ) }

                  <UserApplicationRoles
                    applicationRoles={ values.applicationRoles }
                    onDelete={ ( ar ) =>
                      setFieldValue(
                        'applicationRoles',
                        values.applicationRoles.filter(
                          ( a ) => a.application.id !== ar.application.id && a.role !== ar.role,
                        ),
                      )
                    }
                    className="mt-3"
                  />

                  { errors.applicationRoles && (
                    <Form.Row className={ BEM.$( bem.el( 'application-roles-error' ) ) }>
                      <Col>
                        <Form.Control.Feedback type="invalid">{ errors.applicationRoles }</Form.Control.Feedback>
                      </Col>
                    </Form.Row>
                  ) }

                  <CreateUserApplicationRoles
                    onAdd={ ( applicationRoles ) =>
                      setFieldValue( 'applicationRoles', [...values.applicationRoles, ...applicationRoles] )
                    }
                    className="mt-3"
                  />
                </>
              ) }
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={ onHide } disabled={ loading }>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={ loading }
                onClick={ () => {
                  setStatus( undefined );
                } }
              >
                { loading ? (
                  <>
                    <Spinner animation="border" size="sm" /> Creating...
                  </>
                ) : (
                  'Create User'
                ) }
              </Button>
            </Modal.Footer>
          </Form>
        ) }
      </Formik>
    </Modal>
  );
}

CreateUserModal.displayName = 'CreateUserModal';
